"use client";

import { FC, useState, useEffect, useRef, MouseEvent } from "react";
import axios, { AxiosError } from "axios";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { Quiz } from "@/types/quiz";
import { useAppSelector, useAppDispatch } from "@/redux/store";
import { generateAlert } from "@/redux/actions";
import { useLocalStorage } from "@/hooks/useStorage";
import Modal from "@/components/common/Modal";
import TextField from "@/components/common/TextField";
import Button from "@/components/common/PrimaryButton";
import Logo from "@/components/common/Logo";

async function logInteraction(email: string) {
  try {
    await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/stats/recommended-quiz-signup`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ metadata: { email } }),
      }
    );
  } catch (error: unknown) {
    let errorMessage = "Failed to log recommended quiz interaction";
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    console.error(errorMessage);
  }
}

interface QuizEmailModalProps {
  quiz: Quiz;
  showPopupOverride?: boolean;
  resultUUID?: string;
  delay?: number;
}

const QuizEmailModal: FC<QuizEmailModalProps> = ({
  quiz,
  showPopupOverride,
  resultUUID,
  delay = 2000,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState("");
  const user = useAppSelector((state) => state.user.user);
  const [storedResults] = useLocalStorage("quizResults", []);
  const [emailSubmitted, setEmailSubmitted] = useLocalStorage(
    "emailSubmitted",
    false
  );
  const popupShownRef = useRef(false);
  const showPopup =
    !user &&
    !emailSubmitted &&
    (showPopupOverride ||
      (storedResults &&
        storedResults.some((resultId: string) => resultId === resultUUID)));

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddEmailToList = async (e: MouseEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    if (!email) {
      setInputError("Email is required");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/user/email`, {
        email,
      });
      await logInteraction(email);
      dispatch(
        generateAlert({
          type: "success",
          text: "You have been added to the quiz email list!",
        })
      );
      setEmailSubmitted(true);
      handleClose();
    } catch (error) {
      let errorMessage = "Invalid email";
      console.error(error);
      if (error instanceof AxiosError && error?.response?.data) {
        if (error.response.data.errors) {
          errorMessage = error.response.data.errors[0].msg;
        } else {
          errorMessage = error.response.data;
        }
      }
      setInputError(errorMessage);
    }
    setLoading(false);
  };

  const handleShowPopup = () => {
    if (showPopup && !popupShownRef.current) {
      setOpen(true);
      popupShownRef.current = true;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleShowPopup();
    }, delay);

    return () => clearTimeout(timer);
  }, [showPopup]);

  return (
    <Modal open={open} onClose={handleClose} className={classes.modal}>
      <div className={classes.container}>
        <img
          className={classes.thumbnail}
          src={
            quiz.relatedImageUrl ||
            "https://files.idyllic.app/files/static/idyllic-logo.png"
          }
          alt="Quiz Thumbnail"
        />
        <div className={classes.content}>
          <div className={classes.contentInner}>
            <h6 className={classes.heading}>Love Quizzes?</h6>
            <p className={classes.message}>
              Quizzes you&apos;ll love, straight to your Inbox. Sign up up to
              our mailing list to receive new personalised quizzes as they come
              up!
            </p>
            <form onSubmit={handleAddEmailToList} className={classes.form}>
              <TextField
                value={email}
                onUpdate={setEmail}
                placeholder="Email"
                className={classes.input}
                errorMessage={inputError}
                loading={loading}
                size={isMobile ? "small" : "medium"}
              />
              <Button
                className={classes.button}
                color="secondary"
                type="submit"
                disabled={loading}
              >
                Email Me Quizzes
              </Button>
            </form>
          </div>
          <div className={classes.footer}>
            <img
              className={classes.mobileThumbnail}
              src={
                quiz.relatedImageUrl ||
                "https://files.idyllic.app/files/static/idyllic-logo.png"
              }
              alt="Quiz Thumbnail"
            />
            <div className={classes.brandContainer}>
              <Logo width={isMobile ? "56px" : "70px"} />
              <p className={classes.brandText}>Quizzes Daily</p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default QuizEmailModal;
